import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { AboutNode, Details, Search, TableHeader } from '@/components'
import { makeSearchPath, print, reportsPath, sort } from '@/utils'
import { OrderType } from '@/types'
import { t } from '@/i18n'

import EditReport from './components/editReport'
import ReportRow from './components/reportsRow'
import { useSelect } from './useSelect'
import { useBuildData } from './utils'
import { useData } from './useData'

const columns = [
  {
    key: 'id',
    sortKey: 'id',
  },
  {
    key: 'report_number',
    sortKey: 'reportNumber',
  },
  {
    key: 'car_number',
    sortKey: 'carNumber',
  },
]

const Reports: React.FC = () => {
  const { id, customerId } = useParams()
  const navigate = useNavigate()
  const [sortKey, setKey] = useState<string>()
  const [visible, setVisible] = useState(false)
  const [orderBy, setOrderBy] = useState<OrderType>()
  const _customerId = useMemo(
    () => (customerId === 'customer' ? undefined : Number(customerId)),
    [customerId],
  )
  const { data: selectedData, loading: reportLoading } = useData({ id })
  const { data, loading, onFetchMore, onSearch, pageInfo } =
    useSelect(_customerId)

  useEffect(() => {
    setVisible(false)
  }, [id])

  const sortingBy = useCallback((key?: string, by?: OrderType) => {
    setKey(key)
    setOrderBy(by)
  }, [])

  const onClose = useCallback(() => {
    navigate(makeSearchPath(reportsPath, { customerId: _customerId }))
  }, [navigate, _customerId])

  const HeaderComponent = useMemo(
    () => <Search onSearch={onSearch} />,
    [onSearch],
  )

  return (
    <div className="row">
      <div className={`col w-${!id ? '100' : '45'}`}>
        <Details
          showNoData
          Wrapper="table"
          RowParent="tbody"
          Row={ReportRow}
          loading={loading}
          TitleWrapper="h4"
          title={t('reports')}
          onFetchMore={onFetchMore}
          listWrapperClassName="list-items"
          wrapperClassName="info-box w-100"
          HeaderComponent={HeaderComponent}
          dataBoxWrapperClassName="info-table"
          fetchMoreStatus={pageInfo.fetchMoreStatus}
          data={data.sort((a, b) => sort(a, b, sortKey, orderBy))}
          Header={() => (
            <TableHeader
              columns={columns}
              onSort={sortingBy}
              orderedBy={orderBy}
            />
          )}
        />
      </div>
      <div className="col w-55">
        <AboutNode
          loading={reportLoading}
          sections={[
            {
              title: 'general_info',
              data: useBuildData(selectedData),
              headerButtons: [
                {
                  children: (
                    <>
                      <span className="icon-edit"></span>
                      {t('edit')}
                    </>
                  ),
                  className: 'main-btn outline',
                  onClick: () => setVisible(true),
                },
                {
                  children: (
                    <>
                      <span className="icon-print"></span>
                      {t('print')}
                    </>
                  ),
                  className: 'main-btn light',
                  onClick: () => print('report', selectedData?.id),
                },
              ],
            },
          ]}
          node={visible || !id ? undefined : selectedData}
          onClose={onClose}
        />
        <EditReport
          visible={visible}
          node={selectedData}
          onClose={() => setVisible(false)}
        />
      </div>
    </div>
  )
}

export default Reports
